import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, User, Users, Calendar, Bell, LogOut, Book, ArrowLeft, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './ui/Avatar.jsx';
import { toast } from '../components/notifications';

/**
 * LtiNavBar - Spezialisierte Navigationsleiste für LTI-Benutzer
 * 
 * Zeigt ein angepasstes Branding je nach LMS-Quelle und bietet
 * einen Weg zurück zum LMS sowie eine vereinfachte Navigation
 */
const LtiNavBar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [lmsInfo, setLmsInfo] = useState({
    type: 'unknown',
    name: 'LMS',
    courseName: 'Kurs',
    themeColor: '#3b82f6'  // Standard-Blau
  });

  // LMS-Informationen aus localStorage laden
  useEffect(() => {
    const lmsType = localStorage.getItem('ltiLmsType') || 'unknown';
    const contextLabel = localStorage.getItem('ltiContextLabel') || 'Kurs';
    
    let lmsName = 'LMS';
    let themeColor = '#3b82f6'; // Standardfarbe Blau
    
    // LMS-spezifische Anpassungen
    switch (lmsType.toLowerCase()) {
      case 'moodle':
        lmsName = 'Moodle';
        themeColor = '#f98012'; // Moodle-Orange
        break;
      case 'canvas':
        lmsName = 'Canvas';
        themeColor = '#e72a34'; // Canvas-Rot
        break;
      case 'brightspace':
      case 'd2l':
        lmsName = 'Brightspace';
        themeColor = '#006fbf'; // Brightspace-Blau
        break;
      case 'edx':
        lmsName = 'edX';
        themeColor = '#02262b'; // edX-Dunkelblau
        break;
      case 'blackboard':
        lmsName = 'Blackboard';
        themeColor = '#2b0a3d'; // Blackboard-Lila
        break;
      default:
        lmsName = 'LMS';
        themeColor = '#3b82f6';
    }
    
    setLmsInfo({
      type: lmsType.toLowerCase(),
      name: lmsName,
      courseName: contextLabel,
      themeColor
    });
  }, []);

  // Führt einen LTI-spezifischen Logout durch
  const handleLtiLogout = async () => {
    try {
      // Regulären Logout ausführen
      await logout();
      
      // Da dies eine LTI-Session ist, zeigt eine spezielle Nachricht
      toast.info('Du bist von EachOtter abgemeldet. Du kannst das Fenster schließen oder zum LMS zurückkehren.');
      
      // Optional: zurück zum LMS leiten
      // In einer echten Implementierung könnte hier eine LMS-spezifische Rücksprung-URL verwendet werden
    } catch (error) {
      console.error('Fehler beim LTI-Logout:', error);
      toast.error('Beim Abmelden ist ein Fehler aufgetreten');
    }
  };

  // Zurück zum LMS
  const handleReturnToLms = () => {
    // In einer realen Anwendung würde hier die tatsächliche Return-URL verwendet
    // Diese kann vom LMS als Parameter übergeben werden
    
    // Falls keine spezifische Return-URL bekannt ist, einfach die Authentifizierung beenden
    // und den Benutzer informieren
    toast.info('Zurück zum LMS…');
    
    // Kurze Verzögerung für die Toast-Nachricht
    setTimeout(() => {
      window.close(); // Schließt das Fenster, falls es ein Popup ist
      
      // Fallback: Versuche, einen Redirect zum referrer zu machen
      if (document.referrer && document.referrer.includes(lmsInfo.type)) {
        window.location.href = document.referrer;
      } else {
        // Zeige eine Nachricht, wenn kein automatischer Redirect möglich ist
        toast.info('Bitte geh manuell zum LMS zurück: Seite schließen oder zurück navigieren.');
      }
    }, 1000);
  };

  // Navigationslinks
  const navigationLinks = [
    { path: '/dashboard', icon: <Home size={20} />, label: 'Dashboard' },
    { path: '/profile', icon: <User size={20} />, label: 'Profil' },
    { path: '/sessions', icon: <Calendar size={20} />, label: 'Sitzungen' },
    { path: '/notifications', icon: <Bell size={20} />, label: 'Benachrichtigungen' },
    // Lehrer-spezifische Links
    ...(user?.role === 'TEACHER' || user?.role === 'ADMIN' ? [
      { path: '/teacher', icon: <Book size={20} />, label: 'Lehrerbereich' }
    ] : [])
  ];

  return (
    <nav 
      className="w-full shadow-md z-30 relative"
      style={{ 
        backgroundColor: `${lmsInfo.themeColor}15`, // 15% Opazität für Hintergrund
        borderBottom: `2px solid ${lmsInfo.themeColor}` 
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo und LMS-Branding */}
          <div className="flex items-center">
            <div className="flex items-center">
              <img 
                src="/logo.png" 
                alt="EachOtter Logo" 
                className="h-8 w-auto mr-1"
              />
              <span className="font-bold text-foreground">EachOtter</span>
              
              {/* LMS-Branding */}
              <div 
                className="rounded-xl px-2 py-1 ml-3 text-xs font-medium"
                style={{ 
                  backgroundColor: lmsInfo.themeColor, 
                  color: '#fff'
                }}
              >
                via {lmsInfo.name}
              </div>
            </div>
            
            {/* Kursname (nur auf Desktop) */}
            <div className="hidden sm:ml-4 sm:flex sm:items-center">
              <span className="text-sm text-muted-foreground">
                {lmsInfo.courseName}
              </span>
            </div>
          </div>
          
          {/* Zurück zum LMS Button */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center space-x-4">
            <button
              onClick={handleReturnToLms}
              className="flex items-center px-3 py-1 rounded-xl text-foreground/80 hover:bg-foreground/5 transition-colors text-sm"
            >
              <ArrowLeft size={16} className="mr-1" />
              Zurück zum LMS
            </button>
            
            {/* Desktop-Navigation */}
            <div className="hidden md:flex space-x-1">
              {navigationLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'bg-surface text-foreground shadow-sm'
                      : 'text-foreground/80 hover:bg-foreground/5 hover:text-foreground'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="mr-1.5">{link.icon}</span>
                    <span>{link.label}</span>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Profilbild und Logout-Option */}
            <div className="relative">
              <div className="flex items-center">
                <button
                  onClick={handleLtiLogout}
                  className="flex items-center bg-surface/20 hover:bg-surface/40 px-3 py-1.5 rounded-xl text-foreground/80 border border-border transition-colors text-sm"
                >
                  <LogOut size={16} className="mr-1" />
                  Abmelden
                </button>
              </div>
            </div>
          </div>
          
          {/* Mobile Menü-Button */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-xl text-foreground/80 hover:text-foreground hover:bg-foreground/5 transition-colors"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menü */}
      {isOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {navigationLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-3 py-2 rounded-xl text-base font-medium ${
                  location.pathname === link.path
                    ? `bg-${lmsInfo.themeColor.replace('#', '')}10 text-foreground border-l-4`
                    : 'text-foreground/80 hover:bg-surface-raised hover:text-foreground'
                }`}
                style={{
                  borderLeftColor: location.pathname === link.path ? lmsInfo.themeColor : 'transparent'
                }}
                onClick={() => setIsOpen(false)}
              >
                <div className="flex items-center">
                  <span className="mr-3">{link.icon}</span>
                  <span>{link.label}</span>
                </div>
              </Link>
            ))}
            
            <div className="border-t border-border pt-4 pb-3">
              <div className="flex items-center px-4">
                <div className="flex-shrink-0">
                  <Avatar name={user?.name || 'Nutzer:in'} src={user?.image} size="md" />
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-foreground">{user?.name || 'Nutzer:in'}</div>
                  <div className="text-sm font-medium text-muted-foreground">{user?.email || ''}</div>
                </div>
              </div>
              
              <div className="mt-3 space-y-1">
                <button
                  onClick={handleReturnToLms}
                  className="block w-full text-left px-4 py-2 text-base font-medium text-foreground/80 hover:bg-foreground/5"
                >
                  <div className="flex items-center">
                    <ArrowLeft size={20} className="mr-3" />
                    <span>Zurück zum LMS</span>
                  </div>
                </button>
                <button
                  onClick={handleLtiLogout}
                  className="block w-full text-left px-4 py-2 text-base font-medium text-foreground/80 hover:bg-foreground/5"
                >
                  <div className="flex items-center">
                    <LogOut size={20} className="mr-3" />
                    <span>Abmelden</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default LtiNavBar; 